import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode } from "vue"

import { computed, inject, onMounted, PropType, ref, watch } from 'vue'
import tinycolor from 'tinycolor2'
import { BarChart, LineChart, PieChart } from 'chartist'
import { ChartData, ChartOptions, ChartType } from '@/types/slides'
import { injectKeySlideScale } from '@/types/injectKey'
import { storeToRefs } from 'pinia'
import { useMainStore } from '@/store'
import 'chartist/dist/index.css'


export default /*#__PURE__*/_defineComponent({
  __name: 'Chart',
  props: {
  width: {
    type: Number,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  type: {
    type: String as PropType<ChartType>,
    required: true,
  },
  data: {
    type: Object as PropType<ChartData>,
    required: true,
  },
  options: {
    type: Object as PropType<ChartOptions>,
  },
  themeColor: {
    type: Array as PropType<string[]>,
    required: true,
  },
  legends: {
    type: Array as PropType<string[]>,
    required: true,
  },
  gridColor: {
    type: String,
  },
  labelColor: {
    type: String,
  },
  barWidth: {
    type: Number,
  },
  legend: {
    type: String as PropType<'' | 'top' | 'bottom'>,
  },
},
  setup(__props) {

const props = __props;


const mainStore = useMainStore()
const { canvasScale } = storeToRefs(mainStore)
const chartRef = ref<HTMLElement>()
const slideScale = inject(injectKeySlideScale) || ref(1)

let chart: LineChart | BarChart | PieChart | undefined

const chartHeight = computed(() => {
  if (props.legend) return props.height - 20
  return props.height
})

const getPieChartData = () => ({ ...props.data, series: props.data.series[0] })
const isApple = /Mac|iPod|iPhone|iPad/.test(navigator.platform)
const getOptions = () => {
  const propsOptopns = props.options || {}
  return {
    ...propsOptopns,
     width: slideScale.value == 0.11&&!isApple ? props.width : props.width * slideScale.value,
    height: slideScale.value == 0.11&&!isApple ? chartHeight.value : chartHeight.value * slideScale.value,

  }
}
const renderChart = () => {
  if (!chartRef.value) return
  const options = getOptions()
  if (props.type === 'bar') chart = new BarChart(chartRef.value, props.data, options)
  if (props.type === 'line') chart = new LineChart(chartRef.value, props.data, options)
  if (props.type === 'pie') chart = new PieChart(chartRef.value, getPieChartData(), options)
}

const updateChart = () => {
  if (!chart) {
    renderChart()
    return
  }
  const options = getOptions()
  const data = props.type === 'pie' ? getPieChartData() : props.data
  chart.update(data, options)
}

watch([
  () => props.width,
  () => props.height,
  () => props.data,
  () => props.options,
  slideScale,
], updateChart)
watch(
  [
    () => canvasScale.value,
  ],
  () => {
    updateBarWidth()
    renderChart()
  }

);
onMounted(renderChart)

const themeColors = computed(() => {
  let colors: string[] = []
  if (props.themeColor.length >= 10) colors = props.themeColor
  else if (props.themeColor.length === 1) colors = tinycolor(props.themeColor[0]).analogous(10).map(color => color.toRgbString())
  else {
    const len = props.themeColor.length
    const supplement = tinycolor(props.themeColor[len - 1]).analogous(10 + 1 - len).map(color => color.toRgbString())
    colors = [...props.themeColor.slice(0, len - 1), ...supplement]
  }
  return colors
})

// 更新主题配色：
// 如果当前所设置的主题色数小于10，剩余部分获取最后一个主题色的相近颜色作为配色
const updateTheme = () => {
  if (!chartRef.value) return

  for (let i = 0; i < 10; i++) {
    chartRef.value.style.setProperty(`--theme-color-${i + 1}`, themeColors.value[i])
  }
}

watch(themeColors, updateTheme)
onMounted(updateTheme)

// 更新网格颜色，
const updateGridColor = () => {
  if (!chartRef.value) return
  if (props.gridColor) chartRef.value.style.setProperty(`--grid-color`, props.gridColor)
}

watch(() => props.gridColor, updateGridColor)
onMounted(updateGridColor)

// 更新字体颜色，包括坐标的文字部分
const updateLabelColor = () => {
  if (!chartRef.value) return;
  if (props.labelColor)
    chartRef.value.style.setProperty(`--label-color`, props.labelColor);
};
watch(() => props.labelColor, updateLabelColor);
onMounted(updateLabelColor);

// 更新柱子宽度，包括坐标的文字部分
const updateBarWidth = () => {
  if (slideScale.value == 0.12) return;
  let len = props.data.labels.length
  let barWidth = ((props.width * slideScale.value) - 30) / len / 3;
  if (!chartRef.value) return;
  if (barWidth)
    chartRef.value.style.setProperty(`--bar-width`, barWidth + "px");
};
watch(() => props.labelColor, updateBarWidth);
onMounted(updateBarWidth);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "chart",
    style: _normalizeStyle({ flexDirection: __props.legend === 'top' ? 'column-reverse' : 'column' })
  }, [
    _createElementVNode("div", {
      class: "chart-content",
      ref_key: "chartRef",
      ref: chartRef,
      style: _normalizeStyle({
      width: __props.width + 'px',
      height: _unref(chartHeight) + 'px',
      transform: `scale(${1 / _unref(slideScale)})`,
    })
    }, null, 4),
    (__props.legend)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "legends",
          style: _normalizeStyle({ transform: `scale(${1 / _unref(slideScale)})` })
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.legends, (legend, index) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "legend",
              key: index,
              style: _normalizeStyle({ color: __props.gridColor })
            }, [
              _createElementVNode("div", {
                class: "block",
                style: _normalizeStyle({ backgroundColor: _unref(themeColors)[index] })
              }, null, 4),
              _createTextVNode(" " + _toDisplayString(legend), 1)
            ], 4))
          }), 128))
        ], 4))
      : _createCommentVNode("", true)
  ], 4))
}
}

})